// Maps frontend filter keys → Aurora column names
const FILTER_COL_MAP = {
  gender:    "gender",
  age:       "age",
  ethnicity: "ethnicity",
  education: "education",
  party:     "vote_2023",
};

// Maps frontend integer codes → Aurora string values
// Values that are arrays mean the filter should match ANY of those strings (IN clause)
const FILTER_VAL_MAP = {
  gender:    { 1: "F",               2: "M"                         },
  age:       { 1: "Young",           2: "Middle",    3: "Old"       },
  education: { 1: "High",            2: "Mid",       3: "Low"       },
  ethnicity: {
    1: "European only",
    2: "Māori only",
    3: "Pacific Peoples only",
    4: "Asian only",
    5: "Other",
  },
  party: {
    1: "NAT", 2: "LAB", 3: "ACT",
    4: "GREEN", 5: "NZF", 6: "TPM",
    7: "OTH",  8: "DNV",
  },
};

function translateFilters(rawFilters) {
  if (!rawFilters || typeof rawFilters !== "object") return {};
  const out = {};
  for (const [key, code] of Object.entries(rawFilters)) {
    if (code == null) continue;
    const col = FILTER_COL_MAP[key] || key;
    const valMap = FILTER_VAL_MAP[key];
    const mapped = valMap ? valMap[code] : code;
    if (mapped == null) continue;
    out[col] = mapped; // may be a string or an array of strings
  }
  return out;
}

export async function fetchMetrics({
  level,
  props,
  setState,
  getActiveGroup,
  getFilters,
  getConstCode,
  getKeyWeightMap,
  requestKey,
  _internals,
  tableFromIPC,
  rowsFromTable,
}) {
  const rawGroup = getActiveGroup();
  // In filter mode getActiveGroup() returns { TargetVariable1: group }; unwrap to the leaf group.
  const group = rawGroup?.TargetVariable1 || rawGroup;

  if (!group?.keys?.length) {
    setState({ metricRows: null });
    return;
  }

  const rawFilters = getFilters();
  const filters = translateFilters(rawFilters);
  const constCode = getConstCode?.() ?? null;
  const keyWeightMap = getKeyWeightMap?.() ?? null;
  const reqKey = requestKey(level);

  const now = Date.now();
  if (_internals.lastKey === reqKey && now - _internals.lastAt < 700) return;
  _internals.lastKey = reqKey;
  _internals.lastAt = now;

  if (_internals.inflight) _internals.inflight.abort();
  _internals.inflight = new AbortController();

  const body = {
    groupKeys: group.keys,
    filters,
    areaCodes: [],
    schema: "nz_single",
    turnout_weight: group.weightCol || "n_familiar_fonterra",
    group_by_wave: Boolean(group.waves?.length),
    ...(constCode != null ? { constCode } : {}),
    ...(keyWeightMap ? { keyWeightMap } : {}),
  };

  setState({ loading: true });

  try {
    const res = await fetch(`/api/${level}_data_arrow/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal: _internals.inflight.signal,
    });

    if (!res.ok) throw new Error(`metrics fetch failed ${res.status}`);

    const buf = await res.arrayBuffer();
    const table = tableFromIPC(buf);
    const rows = rowsFromTable(table);

    setState({ metricRows: rows, loading: false });
  } catch (e) {
    if (e.name !== "AbortError") console.error("metrics error:", e);
    setState({ metricRows: null, loading: false });
  } finally {
    _internals.inflight = null;
  }
}
